// shabba-dashboard/src/context/MarketContext.tsx

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import type {
  OrderBookEntry, ParsedMessage, Trade, Position,
  Stats, PriceMap, BookState, Order, OrderFill, PricePoint, AnalyticsData, BookChangePacket
} from '../types';
import { INSTRUMENTS, labelForSecId } from '../constants/instruments';
import { useFixWebSocket } from '../hooks/useFixWebSocket';
import { useAuth } from './AuthContext';
import { parseFixMessage } from '../utils/parseFix';
import { applySnapshot, applyIncremental, bookToArrays, bestPrices } from '../utils/book';
import { eatNow, eatOpenToday, fmtHHmm, sameEatDay } from '../utils/time';
// Analytics come from WebSocket ANALYTICS messages - no client-side calculation needed
import { adaptiveParams } from '../utils/adaptiveParameters';
import { analyticsCacheManager } from '../utils/analyticsCache';
import type { EnhancedAnalyticsData } from '../types';

import { fetchTopLevels } from '../api/book';
import { fetchTradeSeries } from '../api/series';
import { ReplayManager } from '../utils/replayManager';

import type { VWAPDataPoint } from '../components/PriceAreaChart';

function eatISO(d: Date) {
  // EAT = UTC+3; adjust if your runtime isn’t already in EAT
  // If your server/local is already EAT, you can just return d.toISOString()
  return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString();
}
function startOfTodayISO() {
  const now = new Date();
  const s = new Date(now);
  s.setHours(0,0,0,0);
  return eatISO(s);
}
function nowISO() {
  return eatISO(new Date());
}

export type MarketContextValue = {
  // Symbol selection
  selectedSymbol: string;
  setSelectedSymbol: (s: string) => void;

  // Connection state
  connectionStatus: 'connecting' | 'connected' | 'disconnected';
  isAutoTrading: boolean;
  setIsAutoTrading: (b: boolean) => void;

  // Market data
  lastUpdateTime: string;
  currentPrice: number;
  stats: Stats;
  orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] };
  priceMap: PriceMap;
  priceHistory: PricePoint[];
  vwapHistory: VWAPDataPoint[];
  analytics: EnhancedAnalyticsData | null;

  // Trading data
  positions: Position[];
  recentTrades: Trade[];
  orders: Order[];
  messages: ParsedMessage[];
  bookChanges: BookChangePacket[];
  
  // Portfolio and risk (calculated on backend)
  portfolio: {
    totalUnrealizedPnL: number;
    totalRealizedPnL: number;
    totalPnL: number;
    totalExposure: number;
  };
  risk: {
    grossNotional: number;
    longExposure: number;
    shortExposure: number;
  };


  // Actions
  onRequestData: () => void;
  placeOrder: (order: {
    clientOrderId: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    orderType: 'LIMIT' | 'MARKET';
    price?: number;
    quantity: number;
  }) => void;
  testWebSocket: () => void;

  // WebSocket chart data
  twapDataBySymbol: Map<string, Array<{
    timestamp: string;
    twap1m?: number;
    twap5m?: number;
    twap15m?: number;
    twap30m?: number;
    twap1h?: number;
    twap4h?: number;
    lastPrice?: number;
  }>>;
  vwapDataBySymbol: Map<string, Array<{
    timestamp: string;
    vwap: number;
  }>>;
  micropriceDataBySymbol: Map<string, Array<{
    timestamp: string;
    microprice: number;
    imbalance: number;
    spread?: number;
    bidPrice?: number;
    askPrice?: number;
  }>>;
  lastPriceDataBySymbol: Map<string, Array<{
    timestamp: string;
    lastPrice: number;
  }>>;
  dataVersions: Record<string, { twap: number; vwap: number; microprice: number; lastPrice: number }>;

  // Constants and utilities
  INSTRUMENTS: typeof INSTRUMENTS;
  labelForSecId: (s: string) => string;
};

const MarketContext = createContext<MarketContextValue | null>(null);

export function MarketProvider({ children }: { children: React.ReactNode }) {
  // State
  const [selectedSymbol, setSelectedSymbol] = useState<string>('LPPJ');
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [isAutoTrading, setIsAutoTrading] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [priceHistory, setPriceHistory] = useState<PricePoint[]>([]);
  const [vwapHistory, setVwapHistory] = useState<VWAPDataPoint[]>([]);
  const [priceMap, setPriceMap] = useState<PriceMap>({});
  const [stats, setStats] = useState<Stats>({ _cumPxSz: 0, _cumSz: 0 });
  const [orderBook, setOrderBook] = useState<{ bids: OrderBookEntry[]; asks: OrderBookEntry[] }>({ bids: [], asks: [] });
  const [analytics, setAnalytics] = useState<EnhancedAnalyticsData | null>(null);
  const [messages, setMessages] = useState<ParsedMessage[]>([]);
  const [bookChanges, setBookChanges] = useState<BookChangePacket[]>([]);
  const [recentTrades, setRecentTrades] = useState<Trade[]>([
    // { time: '10:02:15', price: 1215.50, size: 500, side: 'buy' },
    // { time: '10:01:42', price: 1214.25, size: 750, side: 'sell' },
    // { time: '10:01:18', price: 1216.75, size: 300, side: 'buy' },
    // { time: '10:00:55', price: 1213.00, size: 1000, side: 'sell' },
    // { time: '10:00:31', price: 1215.25, size: 425, side: 'buy' },
  ]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [portfolio, setPortfolio] = useState<{
    totalUnrealizedPnL: number;
    totalRealizedPnL: number;
    totalPnL: number;
    totalExposure: number;
  }>({
    totalUnrealizedPnL: 0,
    totalRealizedPnL: 0,
    totalPnL: 0,
    totalExposure: 0
  });
  const [risk, setRisk] = useState<{
    grossNotional: number;
    longExposure: number;
    shortExposure: number;
  }>({
    grossNotional: 0,
    longExposure: 0,
    shortExposure: 0
  });
  
  // WebSocket data for charts (TWAP, VWAP, LTP, Microprice)
  const [twapDataBySymbol, setTwapDataBySymbol] = useState<Map<string, Array<{
    timestamp: string;
    twap1m?: number;
    twap5m?: number;
    twap15m?: number;
    twap30m?: number;
    twap1h?: number;
    twap4h?: number;
    lastPrice?: number;
  }>>>(new Map());
  const [vwapDataBySymbol, setVwapDataBySymbol] = useState<Map<string, Array<{
    timestamp: string;
    vwap: number;
  }>>>(new Map());
  const [lastPriceDataBySymbol, setLastPriceDataBySymbol] = useState<Map<string, Array<{
    timestamp: string;
    lastPrice: number;
  }>>>(new Map());
  const [micropriceDataBySymbol, setMicropriceDataBySymbol] = useState<Map<string, Array<{
    timestamp: string;
    microprice: number;
    imbalance: number;
    spread?: number;
    bidPrice?: number;
    askPrice?: number;
  }>>>(new Map());
  
  // Version counters to trigger reactive updates when new data arrives
  // Using a simple object instead of Map for better React reactivity
  const [dataVersions, setDataVersions] = useState<Record<string, { twap: number; vwap: number; microprice: number; lastPrice: number }>>({});

  // Refs for complex state
  const booksBySecIdRef = useRef<Map<string, BookState>>(new Map());
  const statsBySecIdRef = useRef<Map<string, Stats>>(new Map());
  const priceHistBySecIdRef = useRef<Map<string, Array<{ ts: number; price: number }>>>(new Map());
  const lastUpdateBySecIdRef = useRef<Map<string, string>>(new Map());
  const tradesBySecIdRef = useRef<Map<string, Trade[]>>(new Map());
  const lastFillBySecIdRef = useRef<Map<string, { side: 'buy'|'sell'; price: number; size: number; time?: string }>>(new Map());

  // Helper functions
  const ensureBook = useCallback((secId: string): BookState => {
    const m = booksBySecIdRef.current;
    let b = m.get(secId);
    if (!b) { b = { bids: new Map(), asks: new Map() }; m.set(secId, b); }
    return b;
  }, []);

  const ensureStats = useCallback((secId: string): Stats => {
    const m = statsBySecIdRef.current;
    let s = m.get(secId);
    if (!s) { s = { _cumPxSz: 0, _cumSz: 0 }; m.set(secId, s); }
    return s;
  }, []);

  const ensureSeries = useCallback((secId: string) => {
    const m = priceHistBySecIdRef.current;
    let s = m.get(secId);
    if (!s) { s = []; m.set(secId, s); }
    return s;
  }, []);

  const seedFlatSeries = useCallback((secId: string, px: number) => {
    const series = [{ ts: eatOpenToday().getTime(), price: px }, { ts: Date.now(), price: px }];
    priceHistBySecIdRef.current.set(secId, series);
    if (secId === selectedSymbol) {
      setPriceHistory(series.map(p => ({ time: fmtHHmm(p.ts), price: p.price })));
    }
  }, [selectedSymbol]);

  const appendChartPoint = useCallback((secId: string, px: number) => {
    const series = ensureSeries(secId);
    const now = Date.now();
    const last = series[series.length - 1];

    if (last && !sameEatDay(last.ts)) {
      series.length = 0;
    }

    const priceChanged = !last || Math.abs(last.price - px) > 1e-9;
    const timeElapsed = !last || (now - last.ts) >= 5000;

    if (priceChanged || timeElapsed) {
      series.push({ ts: now, price: px });
      if (series.length > 2000) series.shift();
      if (secId === selectedSymbol) {
        setPriceHistory(series.map(p => ({ time: fmtHHmm(p.ts), price: p.price })));
      }
    }
  }, [selectedSymbol, ensureSeries]);

  const extractFix = useCallback((s: string) => {
    const i = s.indexOf("8=");
    return i >= 0 ? s.slice(i) : s;
  }, []);

  // FIX message handler
  const handleFixMessage = useCallback((rawMessage: string) => {
    // JSON envelopes (e.g., BOOK_CHANGES, MARKET_STATS, TWAP from VPS)
    if (rawMessage.length && rawMessage.trim().startsWith('{')) {
      try {
        const obj = JSON.parse(rawMessage);
        
        // Debug logging for order-related messages
        // if (obj?.type === 'ORDER_ACK' || obj?.type === 'EXECUTION_REPORT' || obj?.type === 'ORDER_REJECT' ||
        //     obj?.kind === 'ORDER_ACK' || obj?.kind === 'EXECUTION_REPORT' || obj?.kind === 'ORDER_REJECT') {
        //   console.log('[MarketContext] 📨 Received order message:', obj);
        // }
        
        // Handle ORDER_BOOK (full order book snapshot from JSON)
        if (obj?.kind === 'ORDER_BOOK' && obj.secId && (obj.bids || obj.asks)) {
          const secId = (obj.secId || '').toUpperCase();
          const symbol = obj.symbol || secId;
          
          // Handle bids: can be array or object
          let bids: OrderBookEntry[] = [];
          if (Array.isArray(obj.bids)) {
            // Already an array format
            bids = obj.bids
              .map((entry: any) => ({
                type: "0",
                price: Number(entry.price),
                size: Number(entry.size)
              }))
              .filter((entry: OrderBookEntry) => entry.price > 0 && entry.size > 0)
              .sort((a: OrderBookEntry, b: OrderBookEntry) => b.price - a.price); // Sort descending (highest first)
          } else if (obj.bids && typeof obj.bids === 'object') {
            // Object format (legacy)
            bids = Object.entries(obj.bids)
              .map(([priceStr, size]) => ({
                type: "0",
                price: parseFloat(priceStr),
                size: Number(size)
              }))
              .filter(entry => entry.price > 0 && entry.size > 0)
              .sort((a: OrderBookEntry, b: OrderBookEntry) => b.price - a.price);
          }
          
          // Handle asks: can be array or object
          let asks: OrderBookEntry[] = [];
          if (Array.isArray(obj.asks)) {
            // Already an array format
            asks = obj.asks
              .map((entry: any) => ({
                type: "1",
                price: Number(entry.price),
                size: Number(entry.size)
              }))
              .filter((entry: OrderBookEntry) => entry.price > 0 && entry.size > 0)
              .sort((a: OrderBookEntry, b: OrderBookEntry) => a.price - b.price); // Sort ascending (lowest first)
          } else if (obj.asks && typeof obj.asks === 'object') {
            // Object format (legacy)
            asks = Object.entries(obj.asks)
              .map(([priceStr, size]) => ({
                type: "1",
                price: parseFloat(priceStr),
                size: Number(size)
              }))
              .filter(entry => entry.price > 0 && entry.size > 0)
              .sort((a: OrderBookEntry, b: OrderBookEntry) => a.price - b.price);
          }
          
          // Update order book if this is the selected symbol
          if (secId === selectedSymbol) {
            setOrderBook({ bids, asks });
            
            // Update last update time
            let timestamp: string;
            if (obj.timestamp_iso) {
              timestamp = obj.timestamp_iso;
            } else if (obj.timestamp) {
              if (typeof obj.timestamp === 'string') {
                timestamp = obj.timestamp;
              } else if (obj.timestamp < 1e12) {
                timestamp = new Date(obj.timestamp * 1000).toISOString();
              } else {
                timestamp = new Date(obj.timestamp).toISOString();
              }
            } else {
              timestamp = new Date().toISOString();
            }
            
            lastUpdateBySecIdRef.current.set(secId, timestamp);
            setLastUpdateTime(timestamp);
            
            // Update current price (use mid price or best bid/ask)
            if (bids.length > 0 && asks.length > 0) {
              const bestBid = bids[0].price;
              const bestAsk = asks[0].price;
              const midPrice = (bestBid + bestAsk) / 2;
              setCurrentPrice(midPrice);
            } else if (bids.length > 0) {
              setCurrentPrice(bids[0].price);
            } else if (asks.length > 0) {
              setCurrentPrice(asks[0].price);
            }
          }
          
          // Also update the internal book state for analytics
          const book: BookState = {
            bids: new Map(),
            asks: new Map()
          };
          
          bids.forEach(bid => {
            book.bids.set(`px:${bid.price}`, { price: bid.price, size: bid.size });
          });
          
          asks.forEach(ask => {
            book.asks.set(`px:${ask.price}`, { price: ask.price, size: ask.size });
          });
          
          booksBySecIdRef.current.set(secId, book);
          
          // Use analytics from ORDER_BOOK message if available
          if (obj.analytics && secId === selectedSymbol) {
            // Update current price from analytics if available
            if (obj.analytics.midPrice != null && Number.isFinite(obj.analytics.midPrice)) {
              setCurrentPrice(Number(obj.analytics.midPrice));
            } else if (obj.analytics.bestBid != null && obj.analytics.bestAsk != null) {
              const mid = (Number(obj.analytics.bestBid) + Number(obj.analytics.bestAsk)) / 2;
              setCurrentPrice(mid);
            }
          }
          
          return;
        }
        
        // Handle ANALYTICS message (comprehensive analytics from VPS)
        if (obj?.kind === 'ANALYTICS' && obj.secId && obj.analytics) {
          const secId = (obj.secId || '').toUpperCase();
          if (!secId) return;
          
          // Handle timestamp
          let ts: string;
          if (obj.timestamp) {
            if (typeof obj.timestamp === 'string') {
              ts = obj.timestamp;
            } else if (obj.timestamp < 1e12) {
              ts = new Date(obj.timestamp * 1000).toISOString();
            } else {
              ts = new Date(obj.timestamp).toISOString();
            }
          } else {
            ts = new Date().toISOString();
          }
          
          // Convert VPS analytics format to client format
          const analyticsData: EnhancedAnalyticsData = {
            depthImbalance: obj.analytics.depth || obj.analytics.depthImbalance ? {
              topN: obj.analytics.depth?.topN || obj.analytics.depthImbalance?.topN || 5,
              bidVolume: obj.analytics.depth?.bidVolume || obj.analytics.depthImbalance?.bidVolume || 0,
              askVolume: obj.analytics.depth?.askVolume || obj.analytics.depthImbalance?.askVolume || 0,
              imbalance: obj.analytics.depth?.imbalance || obj.analytics.depthImbalance?.imbalance || 0,
              imbalancePercent: obj.analytics.depth?.imbalancePercent || obj.analytics.depthImbalance?.imbalancePercent || 0
            } : {
              topN: 5,
              bidVolume: 0,
              askVolume: 0,
              imbalance: 0,
              imbalancePercent: 0
            },
            microprice: obj.analytics.microprice ? {
              microprice: obj.analytics.microprice.value || obj.analytics.microprice.microprice || 0,
              bidPrice: obj.analytics.microprice.bidPrice || 0,
              askPrice: obj.analytics.microprice.askPrice || 0,
              bidSize: obj.analytics.microprice.bidSize || 0,
              askSize: obj.analytics.microprice.askSize || 0,
              spread: obj.analytics.microprice.spread || 0
            } : {
              microprice: 0,
              bidPrice: 0,
              askPrice: 0,
              bidSize: 0,
              askSize: 0,
              spread: 0
            },
            staleness: obj.analytics.staleness ? {
              lastUpdateAge: obj.analytics.staleness.lastUpdateAge || 0,
              lastTradeAge: obj.analytics.staleness.lastTradeAge || 0,
              isStale: obj.analytics.staleness.isStale || false,
              stalenessLevel: obj.analytics.staleness.stalenessLevel || 'fresh'
            } : {
              lastUpdateAge: 0,
              lastTradeAge: 0,
              isStale: false,
              stalenessLevel: 'fresh'
            },
            orderFlowImbalance: obj.analytics.orderFlow ? {
              buyFlow: obj.analytics.orderFlow.buyFlow || 0,
              sellFlow: obj.analytics.orderFlow.sellFlow || 0,
              netFlow: obj.analytics.orderFlow.netFlow || 0,
              flowRatio: obj.analytics.orderFlow.flowRatio || 0,
              timeWindow: obj.analytics.orderFlow.timeWindow || 300
            } : {
              buyFlow: 0,
              sellFlow: 0,
              netFlow: 0,
              flowRatio: 0,
              timeWindow: 300
            },
            fillProbability: obj.analytics.fillProbability ? {
              probability: obj.analytics.fillProbability.probability || 0,
              queuePosition: obj.analytics.fillProbability.queuePosition || 0,
              estimatedWaitTime: obj.analytics.fillProbability.estimatedWaitTime || 0,
              confidence: obj.analytics.fillProbability.confidence || 'low'
            } : {
              probability: 0,
              queuePosition: 0,
              estimatedWaitTime: 0,
              confidence: 'low'
            },
            volatility: obj.analytics.volatility ? {
              realizedVol: obj.analytics.volatility.realizedVol || 0,
              impliedVol: obj.analytics.volatility.impliedVol || 0,
              volatilityPercentile: obj.analytics.volatility.volatilityPercentile || 0,
              recommendedSpread: obj.analytics.volatility.recommendedSpread || 0,
              spreadMultiplier: obj.analytics.volatility.spreadMultiplier || 1.2
            } : {
              realizedVol: 0,
              impliedVol: 0,
              volatilityPercentile: 0,
              recommendedSpread: 0,
              spreadMultiplier: 1.2
            },
            liquidityHeatmap: obj.analytics.liquidityHeatmap ? {
              priceLevels: (obj.analytics.liquidityHeatmap.levels || []).map((level: any) => ({
                price: level.price || 0,
                bidLiquidity: level.side === 'bid' ? (level.size || 0) : 0,
                askLiquidity: level.side === 'ask' ? (level.size || 0) : 0,
                totalLiquidity: level.size || 0,
                heatScore: 0.5 // Default heat score
              })),
              maxLiquidity: obj.analytics.liquidityHeatmap.totalLiquidity || 0,
              avgLiquidity: obj.analytics.liquidityHeatmap.totalLiquidity ? 
                (obj.analytics.liquidityHeatmap.totalLiquidity / Math.max(1, (obj.analytics.liquidityHeatmap.levels?.length || 1))) : 0
            } : {
              priceLevels: [],
              maxLiquidity: 0,
              avgLiquidity: 0
            },
            vwapAnalysis: obj.analytics.vwap ? {
              currentVWAP: obj.analytics.vwap.value || obj.analytics.vwap.currentVWAP || 0,
              distance: obj.analytics.vwap.distance || 0,
              distancePercent: obj.analytics.vwap.distancePercent || 0,
              volume: obj.analytics.vwap.volume || 0,
              trend: obj.analytics.vwap.trend || 'neutral'
            } : {
              currentVWAP: 0,
              distance: 0,
              distancePercent: 0,
              volume: 0,
              trend: 'neutral'
            },
            bookPressure: obj.analytics.bookPressure ? {
              pressure: obj.analytics.bookPressure.pressure || 0,
              buyPressure: obj.analytics.bookPressure.buyPressure || 0,
              sellPressure: obj.analytics.bookPressure.sellPressure || 0,
              netPressure: obj.analytics.bookPressure.netPressure || 0,
              confidenceLevel: obj.analytics.bookPressure.confidenceLevel || 'low'
            } : {
              pressure: 0,
              buyPressure: 0,
              sellPressure: 0,
              netPressure: 0,
              confidenceLevel: 'low'
            },
            spreadAnalysis: obj.analytics.spread ? {
              currentSpread: obj.analytics.spread.currentSpread || 0,
              averageSpread: obj.analytics.spread.averageSpread || 0,
              spreadPercentile: obj.analytics.spread.spreadPercentile || 0,
              isWide: obj.analytics.spread.isWide || false,
              competitiveness: obj.analytics.spread.competitiveness || 'normal',
              optimalBid: obj.analytics.spread.optimalBid || 0,
              optimalAsk: obj.analytics.spread.optimalAsk || 0
            } : {
              currentSpread: 0,
              averageSpread: 0,
              spreadPercentile: 0,
              isWide: false,
              competitiveness: 'normal',
              optimalBid: 0,
              optimalAsk: 0
            },
            riskMetrics: obj.analytics.risk ? {
              inventoryRisk: obj.analytics.risk.inventoryRisk || 0,
              priceRisk: obj.analytics.risk.priceRisk || 0,
              liquidityRisk: obj.analytics.risk.liquidityRisk || 0,
              overallRisk: (() => {
                const risk = obj.analytics.risk.overallRisk;
                if (typeof risk === 'string') {
                  const lower = risk.toLowerCase();
                  if (lower === 'low' || lower === 'medium' || lower === 'high') {
                    return lower as 'low' | 'medium' | 'high';
                  }
                }
                // If it's a number, convert to risk level
                if (typeof risk === 'number') {
                  if (risk >= 0.7) return 'high';
                  if (risk >= 0.4) return 'medium';
                  return 'low';
                }
                return 'low';
              })(),
              maxExposure: obj.analytics.risk.maxExposure || 0,
              recommendedPosition: obj.analytics.risk.recommendedPosition || 0
            } : {
              inventoryRisk: 0,
              priceRisk: 0,
              liquidityRisk: 0,
              overallRisk: 'low',
              maxExposure: 0,
              recommendedPosition: 0
            },
            fullBookImbalance: obj.analytics.fullBookImbalance || obj.analytics.depth?.fullBookImbalance ? {
              bidVolume: obj.analytics.fullBookImbalance?.bidVolume || obj.analytics.depth?.fullBookImbalance?.bidVolume || 0,
              askVolume: obj.analytics.fullBookImbalance?.askVolume || obj.analytics.depth?.fullBookImbalance?.askVolume || 0,
              imbalance: obj.analytics.fullBookImbalance?.imbalance || obj.analytics.depth?.fullBookImbalance?.imbalance || 0,
              imbalancePercent: obj.analytics.fullBookImbalance?.imbalancePercent || obj.analytics.depth?.fullBookImbalance?.imbalancePercent || 0
            } : {
              bidVolume: 0,
              askVolume: 0,
              imbalance: 0,
              imbalancePercent: 0
            },
            timestamp: Date.now()
          };
          
          // Update analytics for selected symbol
          if (secId === selectedSymbol) {
            setAnalytics(analyticsData);
          }
          
          // Update last update time
          lastUpdateBySecIdRef.current.set(secId, ts);
          if (secId === selectedSymbol) {
            setLastUpdateTime(ts);
          }
          
          return;
        }
        
        // Handle RECENT_TRADES message
        if (obj?.kind === 'RECENT_TRADES' && obj.secId && Array.isArray(obj.trades)) {
          const secId = (obj.secId || '').toUpperCase();
          if (!secId) return;
          
          // Convert VPS trade format to client format
          const trades: Trade[] = obj.trades.map((t: any) => ({
            time: t.time || new Date().toISOString(),
            price: Number(t.price) || 0,
            size: Number(t.size) || 0,
            side: (t.side?.toLowerCase() === 'sell' || t.side === 'SELL') ? 'sell' : 'buy',
            symbol: secId
          })).filter((t: Trade) => t.price > 0 && t.size > 0);
          
          // Store trades per symbol
          tradesBySecIdRef.current.set(secId, trades);
          
          // Update recent trades for selected symbol
          if (secId === selectedSymbol) {
            setRecentTrades(trades.slice(0, 10));
            
            // Update current price from last trade if available
            if (trades.length > 0) {
              const lastTrade = trades[0];
              setCurrentPrice(lastTrade.price);
            }
          }
          
          // Handle timestamp
          let ts: string;
          if (obj.timestamp) {
            if (typeof obj.timestamp === 'string') {
              ts = obj.timestamp;
            } else if (obj.timestamp < 1e12) {
              ts = new Date(obj.timestamp * 1000).toISOString();
            } else {
              ts = new Date(obj.timestamp).toISOString();
            }
          } else {
            ts = new Date().toISOString();
          }
          
          lastUpdateBySecIdRef.current.set(secId, ts);
          if (secId === selectedSymbol) {
            setLastUpdateTime(ts);
          }
          
          return;
        }
        
        // Handle BOOK_CHANGES
        if (obj?.kind === 'BOOK_CHANGES' && Array.isArray(obj.changes)) {
          // Normalize timestamp for real-time messages (same as replay processing)
          let normalizedTimestamp: string;
          if (obj.timestamp) {
            if (typeof obj.timestamp === 'string') {
              normalizedTimestamp = obj.timestamp;
            } else if (obj.timestamp < 1e12) {
              // Unix seconds
              normalizedTimestamp = new Date(obj.timestamp * 1000).toISOString();
            } else {
              // Unix milliseconds
              normalizedTimestamp = new Date(obj.timestamp).toISOString();
            }
          } else {
            normalizedTimestamp = new Date().toISOString();
          }
          
          // Ensure secId is uppercase for consistency
          const normalizedSecId = (obj.secId || '').toUpperCase();
          
          // Create normalized packet
          const normalizedPacket: BookChangePacket = {
            kind: 'BOOK_CHANGES',
            secId: normalizedSecId,
            symbol: obj.symbol,
            timestamp: normalizedTimestamp,
            changes: obj.changes
          };
          
          // Add new real-time book changes (prepend to array)
          // Don't limit here - let the component handle display limits
          setBookChanges(prev => {
            // Check if this packet already exists (avoid duplicates from replay)
            const exists = prev.some(pkt => 
              pkt.timestamp === normalizedTimestamp && 
              pkt.secId === normalizedSecId &&
              pkt.changes.length === normalizedPacket.changes.length
            );
            if (exists) return prev;
            return [normalizedPacket, ...prev];
          });
          // Update last update time using packet timestamp per selected symbol
          if (normalizedSecId) {
            lastUpdateBySecIdRef.current.set(normalizedSecId, normalizedTimestamp);
            if (normalizedSecId === selectedSymbol.toUpperCase()) {
              setLastUpdateTime(normalizedTimestamp);
            }
            // Determine last negative change (fill) and store as last fill
            const neg = (obj.changes || [])
              .map((c: any) => {
                const oldVal = Number.isFinite(c.old) ? Number(c.old) : Number.isFinite(c.previous) ? Number(c.previous) : Number.isFinite(c.prev) ? Number(c.prev) : NaN;
                const newVal = Number.isFinite(c.new) ? Number(c.new) : Number.isFinite(c.size) ? Number(c.size) : NaN;
                if (!Number.isFinite(oldVal) || !Number.isFinite(newVal) || oldVal === newVal) return null;
                const delta = newVal - oldVal;
                if (delta >= 0) return null;
                return { side: c.side as 'BID'|'ASK', price: Number(c.price), size: Math.abs(delta) };
              })
              .filter(Boolean) as Array<{ side: 'BID'|'ASK'; price: number; size: number }>;
            if (neg.length > 0) {
              const lastNeg = neg[0];
              const side = lastNeg.side === 'BID' ? 'buy' : 'sell';
              lastFillBySecIdRef.current.set(obj.secId.toUpperCase(), { side, price: lastNeg.price, size: lastNeg.size, time: obj.timestamp });
            }
          }
          return;
        }
        
        // Handle MARKET_STATS (contains VWAP and last price)
        if (obj?.kind === 'MARKET_STATS' && obj.secId && obj.stats) {
          const secId = obj.secId.toUpperCase();
          // Handle timestamp: could be Unix seconds, milliseconds, or ISO string
          let ts: string;
          if (obj.timestamp) {
            if (typeof obj.timestamp === 'string') {
              ts = obj.timestamp;
            } else if (obj.timestamp < 1e12) {
              // Unix timestamp in seconds
              ts = new Date(obj.timestamp * 1000).toISOString();
            } else {
              // Unix timestamp in milliseconds
              ts = new Date(obj.timestamp).toISOString();
            }
          } else {
            ts = new Date().toISOString();
          }
          
          // Update stats
          const stats = ensureStats(secId);
          const updatedStats: Stats = { ...stats };
          if (Number.isFinite(obj.stats.vwap)) updatedStats.vwap = Number(obj.stats.vwap);
          if (Number.isFinite(obj.stats.last)) updatedStats.last = Number(obj.stats.last);
          if (Number.isFinite(obj.stats.volume)) updatedStats.volume = Number(obj.stats.volume);
          if (Number.isFinite(obj.stats.open)) updatedStats.open = Number(obj.stats.open);
          if (Number.isFinite(obj.stats.high)) updatedStats.high = Number(obj.stats.high);
          if (Number.isFinite(obj.stats.low)) updatedStats.low = Number(obj.stats.low);
          if (Number.isFinite(obj.stats.close)) updatedStats.close = Number(obj.stats.close);
          if (Number.isFinite(obj.stats.prev_close)) updatedStats.prevClose = Number(obj.stats.prev_close);
          statsBySecIdRef.current.set(secId, updatedStats);
          if (secId === selectedSymbol) {
            setStats(updatedStats);
            if (Number.isFinite(obj.stats.last)) setCurrentPrice(Number(obj.stats.last));
          }
          
          // Store VWAP data for charts
          if (Number.isFinite(obj.stats.vwap)) {
            setVwapDataBySymbol(prev => {
              const newMap = new Map(prev);
              const existing = newMap.get(secId) || [];
              
              // Check if this timestamp already exists (avoid duplicates)
              const timestampExists = existing.some(p => p.timestamp === ts);
              if (timestampExists) {
                return prev; // No change, return previous map to avoid re-render
              }
              
              const newData = [...existing, { timestamp: ts, vwap: Number(obj.stats.vwap) }];
              // Keep last 200 points
              newMap.set(secId, newData.slice(-200));
              
              // Only increment version if we actually added new data
              setDataVersions(prev => ({
                ...prev,
                [secId]: {
                  ...prev[secId],
                  vwap: ((prev[secId]?.vwap || 0) + 1)
                }
              }));
              
              return newMap;
            });
          }
          
          // Update last update time
          lastUpdateBySecIdRef.current.set(secId, ts);
          if (secId === selectedSymbol) {
            setLastUpdateTime(ts);
          }
          
          return;
        }
        
        // Handle TWAP messages (contains TWAPs for different time windows)
        if (obj?.kind === 'TWAP' && obj.secId && obj.twaps) {
          const secId = obj.secId.toUpperCase();
          // Handle timestamp: could be Unix seconds, milliseconds, or ISO string
          let ts: string;
          if (obj.timestamp) {
            if (typeof obj.timestamp === 'string') {
              ts = obj.timestamp;
            } else if (obj.timestamp < 1e12) {
              // Unix timestamp in seconds
              ts = new Date(obj.timestamp * 1000).toISOString();
            } else {
              // Unix timestamp in milliseconds
              ts = new Date(obj.timestamp).toISOString();
            }
          } else {
            ts = new Date().toISOString();
          }
          
          // Extract TWAP values from the twaps object
          const twapData: {
            timestamp: string;
            twap1m?: number;
            twap5m?: number;
            twap15m?: number;
            twap30m?: number;
            twap1h?: number;
            twap4h?: number;
            lastPrice?: number;
          } = { timestamp: ts };
          
          // Map the twaps object keys to our format
          if (Number.isFinite(obj.twaps['1m'])) twapData.twap1m = Number(obj.twaps['1m']);
          if (Number.isFinite(obj.twaps['5m'])) twapData.twap5m = Number(obj.twaps['5m']);
          if (Number.isFinite(obj.twaps['15m'])) twapData.twap15m = Number(obj.twaps['15m']);
          if (Number.isFinite(obj.twaps['30m'])) twapData.twap30m = Number(obj.twaps['30m']);
          if (Number.isFinite(obj.twaps['1hr'])) twapData.twap1h = Number(obj.twaps['1hr']);
          if (Number.isFinite(obj.twaps['4hr'])) twapData.twap4h = Number(obj.twaps['4hr']);
          
          // Store TWAP data for charts
          setTwapDataBySymbol(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(secId) || [];
            
            // Check if this timestamp already exists (avoid duplicates)
            const timestampExists = existing.some(p => p.timestamp === ts);
            if (timestampExists) {
              return prev; // No change, return previous map to avoid re-render
            }
            
            const newData = [...existing, twapData];
            // Keep last 200 points
            newMap.set(secId, newData.slice(-200));
            
            // Only increment version if we actually added new data
            setDataVersions(prev => ({
              ...prev,
              [secId]: {
                ...prev[secId],
                twap: ((prev[secId]?.twap || 0) + 1)
              }
            }));
            
            return newMap;
          });
          
          // Update last update time
          lastUpdateBySecIdRef.current.set(secId, ts);
          if (secId === selectedSymbol) {
            setLastUpdateTime(ts);
          }
          
          return;
        }
        
        // Handle VWAMP messages (Volume-Weighted Average Microprice)
        if (obj?.kind === 'VWAMP' && (obj.secId || obj.symbol) && Number.isFinite(obj.vwamp)) {
          const secId = (obj.secId || obj.symbol || '').toUpperCase();
          if (!secId) return;
          
          // Handle timestamp: could be Unix seconds, milliseconds, or ISO string
          let ts: string;
          if (obj.timestamp) {
            if (typeof obj.timestamp === 'string') {
              ts = obj.timestamp;
            } else if (obj.timestamp < 1e12) {
              // Unix timestamp in seconds
              ts = new Date(obj.timestamp * 1000).toISOString();
            } else {
              // Unix timestamp in milliseconds
              ts = new Date(obj.timestamp).toISOString();
            }
          } else {
            ts = new Date().toISOString();
          }
          
          // Store microprice data for charts (using vwamp as microprice)
          setMicropriceDataBySymbol(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(secId) || [];
            
            // Check if this timestamp already exists (avoid duplicates)
            const timestampExists = existing.some(p => p.timestamp === ts);
            if (timestampExists) {
              return prev; // No change, return previous map to avoid re-render
            }
            
            const micropriceData = {
              timestamp: ts,
              microprice: Number(obj.vwamp), // VWAMP is the microprice value
              imbalance: Number(obj.imbalance || 0),
              spread: Number.isFinite(obj.spread) ? Number(obj.spread) : undefined,
              bidPrice: Number.isFinite(obj.bidPrice) ? Number(obj.bidPrice) : undefined,
              askPrice: Number.isFinite(obj.askPrice) ? Number(obj.askPrice) : undefined
            };
            
            const newData = [...existing, micropriceData];
            // Keep all data for imbalance chart reconstruction (no limit)
            newMap.set(secId, newData);
            
            // Only increment version if we actually added new data
            setDataVersions(prevVersions => ({
              ...prevVersions,
              [secId]: {
                ...prevVersions[secId],
                microprice: ((prevVersions[secId]?.microprice || 0) + 1)
              }
            }));
            
            return newMap;
          });
          
          // Update last update time
          lastUpdateBySecIdRef.current.set(secId, ts);
          if (secId === selectedSymbol) {
            setLastUpdateTime(ts);
          }
          
          return;
        }
        
        // Also handle MICROPRICE messages (for backward compatibility)
        if (obj?.kind === 'MICROPRICE' && (obj.secId || obj.symbol) && Number.isFinite(obj.microprice)) {
          const secId = (obj.secId || obj.symbol || '').toUpperCase();
          if (!secId) return;
          
          // Handle timestamp: could be Unix seconds, milliseconds, or ISO string
          let ts: string;
          if (obj.timestamp) {
            if (typeof obj.timestamp === 'string') {
              ts = obj.timestamp;
            } else if (obj.timestamp < 1e12) {
              // Unix timestamp in seconds
              ts = new Date(obj.timestamp * 1000).toISOString();
            } else {
              // Unix timestamp in milliseconds
              ts = new Date(obj.timestamp).toISOString();
            }
          } else {
            ts = new Date().toISOString();
          }
          
          // Store microprice data for charts
          setMicropriceDataBySymbol(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(secId) || [];
            
            // Check if this timestamp already exists (avoid duplicates)
            const timestampExists = existing.some(p => p.timestamp === ts);
            if (timestampExists) {
              return prev; // No change, return previous map to avoid re-render
            }
            
            const micropriceData = {
              timestamp: ts,
              microprice: Number(obj.microprice),
              imbalance: Number(obj.imbalance || 0),
              spread: Number.isFinite(obj.spread) ? Number(obj.spread) : undefined,
              bidPrice: Number.isFinite(obj.bidPrice) ? Number(obj.bidPrice) : undefined,
              askPrice: Number.isFinite(obj.askPrice) ? Number(obj.askPrice) : undefined
            };
            
            const newData = [...existing, micropriceData];
            // Keep all data for imbalance chart reconstruction (no limit)
            newMap.set(secId, newData);
            
            // Only increment version if we actually added new data
            setDataVersions(prevVersions => ({
              ...prevVersions,
              [secId]: {
                ...prevVersions[secId],
                microprice: ((prevVersions[secId]?.microprice || 0) + 1)
              }
            }));
            
            return newMap;
          });
          
          // Update last update time
          lastUpdateBySecIdRef.current.set(secId, ts);
          if (secId === selectedSymbol) {
            setLastUpdateTime(ts);
          }
          
          return;
        }
        
        // Handle ORDER_ACK (order acknowledgment)
        // Support multiple formats: obj.order or direct properties, type or kind field
        if (obj?.type === 'ORDER_ACK' || obj?.kind === 'ORDER_ACK') {
          // Support both nested (obj.order) and flat (obj directly) formats
          const orderAck = obj.order || obj;
          const clientOrderId = orderAck.clientOrderId || orderAck.id || orderAck.orderId || obj.orderId;
          const exchangeOrderId = orderAck.orderId || obj.orderId; // Exchange-assigned order ID
          const symbol = (orderAck.symbol || obj.symbol || '').toUpperCase();
          
          if (clientOrderId && symbol) {
            // Map status from message (NEW, FILLED, etc.) to client format
            const statusFromMessage = (orderAck.status || obj.status || '').toUpperCase();
            let orderStatus: 'new' | 'filled' | 'rejected' | 'partially_filled' = 'new';
            if (statusFromMessage === 'FILLED' || statusFromMessage === '2') {
              orderStatus = 'filled';
            } else if (statusFromMessage === 'PARTIALLY_FILLED' || statusFromMessage === '1') {
              orderStatus = 'partially_filled';
            } else if (statusFromMessage === 'REJECTED' || statusFromMessage === '8') {
              orderStatus = 'rejected';
            } else if (statusFromMessage === 'NEW' || statusFromMessage === '0' || !statusFromMessage) {
              orderStatus = 'new';
            }
            
            const orderQty = Number(orderAck.quantity || orderAck.qty || obj.quantity || obj.qty || 0);
            const newOrder: Order = {
              id: clientOrderId,
              exchangeOrderId: exchangeOrderId || undefined,
              symbol: symbol,
              side: ((orderAck.side || obj.side)?.toUpperCase() === 'SELL' || (orderAck.side || obj.side)?.toLowerCase() === 'sell') ? 'sell' : 'buy',
              qty: orderQty,
              price: Number(orderAck.price || orderAck.limitPrice || obj.price || obj.limitPrice || 0),
              status: orderStatus,
              time: orderAck.time || orderAck.timestamp || obj.time || obj.timestamp || new Date().toISOString(),
              fills: [],                    // Initialize empty fills array
              cumQty: 0,                    // Initialize cumulative filled quantity
              leavesQty: orderQty           // Initialize remaining quantity
            };
            
            // console.log('[MarketContext] ✅ Order ACK received:', {
            //   clientOrderId,
            //   exchangeOrderId,
            //   symbol,
            //   side: newOrder.side,
            //   quantity: newOrder.qty,
            //   price: newOrder.price,
            //   status: newOrder.status
            // });
            
            setOrders(prev => {
              // Update existing order or add new one
              const existingIndex = prev.findIndex(o => o.id === clientOrderId);
              if (existingIndex >= 0) {
                // Update existing order, preserve fills if they exist
                const updated = [...prev];
                const existing = updated[existingIndex];
                updated[existingIndex] = { 
                  ...existing,
                  ...newOrder,
                  // Preserve fills and cumulative values if order already has them
                  fills: existing.fills || [],
                  cumQty: existing.cumQty ?? 0,
                  leavesQty: existing.leavesQty ?? newOrder.leavesQty
                };
                return updated;
              }
              // Add new order
              return [newOrder, ...prev];
            });
          } else {
            console.warn('[MarketContext] ⚠️ ORDER_ACK missing required fields:', { clientOrderId, symbol, orderAck });
          }
          return;
        }
        
        // Handle ORDER_REJECT
        if (obj?.type === 'ORDER_REJECT' || obj?.type === 'REJECT' || 
            obj?.kind === 'ORDER_REJECT' || obj?.kind === 'REJECT') {
          const reject = obj.order || obj.rejection || obj;
          const clientOrderId = reject.clientOrderId || reject.id || reject.orderId;
          
          if (clientOrderId) {
            // console.log('[MarketContext] ❌ Order rejected:', clientOrderId, reject.reason || reject.message);
            
            setOrders(prev => prev.map(o => {
              if (o.id === clientOrderId) {
                return { ...o, status: 'rejected' };
              }
              return o;
            }));
          }
          return;
        }
        
        // Handle EXECUTION_REPORT (order fill/partial fill)
        // Support multiple formats: obj.execution, obj.kind, or direct properties
        if (obj?.type === 'EXECUTION_REPORT' || obj?.type === 'FILL' || obj?.type === 'TRADE' ||
            obj?.kind === 'EXECUTION_REPORT' || obj?.kind === 'FILL' || obj?.kind === 'TRADE') {
          // Support both nested (obj.execution) and flat (obj directly) formats
          const exec = obj.execution || obj.fill || obj.trade || obj;
          const clientOrderId = exec.clientOrderId || exec.orderId || exec.id || obj.orderId;
          const symbol = (exec.symbol || obj.symbol || '').toUpperCase();
          // Extract price - check both exec and obj level
          const execPrice = Number(exec.price || exec.execPrice || exec.fillPrice || obj.price || 0);
          // Extract cumulative and remaining quantities
          const cumQty = Number(exec.cumQty || exec.execQty || exec.fillQty || obj.cumQty || 0);
          const leavesQty = Number(exec.leavesQty || obj.leavesQty || 0);
          // Extract fill quantity (increment for this event) - prefer quantity field, fallback to calculated
          const fillQty = Number(exec.quantity || obj.quantity || 0);
          // If quantity not provided, calculate from cumQty difference
          const execQty = fillQty > 0 ? fillQty : cumQty; // Use fillQty if available, otherwise use cumQty as fallback
          const execSide = ((exec.side || obj.side)?.toUpperCase() === 'SELL' || (exec.side || obj.side) === 'sell') ? 'sell' : 'buy';
          const fillTime = exec.time || exec.timestamp || obj.time || obj.timestamp || new Date().toISOString();
          const fillValue = exec.value || (execPrice * execQty);
          
          // Handle FIX ordStatus codes: 0=New, 1=PartiallyFilled, 2=Filled, 4=Canceled, 8=Rejected
          // Check both exec and obj level for ordStatus
          const ordStatusValue = exec.ordStatus !== undefined ? exec.ordStatus : (obj.ordStatus !== undefined ? obj.ordStatus : undefined);
          let orderStatus = exec.status || exec.orderStatus || exec.fillStatus || obj.status;
          if (ordStatusValue !== undefined) {
            const ordStatusMap: Record<string, string> = {
              '0': 'new',
              '1': 'partially_filled',
              '2': 'filled',
              '4': 'cancelled',
              '8': 'rejected'
            };
            orderStatus = ordStatusMap[String(ordStatusValue)] || 'new';
          }
          if (!orderStatus) {
            // Determine status from leavesQty and cumQty if available
            const leavesQty = Number(exec.leavesQty || obj.leavesQty || 0);
            const cumQty = Number(exec.cumQty || obj.cumQty || 0);
            if (cumQty > 0 && leavesQty === 0) {
              orderStatus = 'filled';
            } else if (cumQty > 0 && leavesQty > 0) {
              orderStatus = 'partially_filled';
            } else {
              orderStatus = 'new';
            }
          }
          
          // console.log('[MarketContext] 📊 Execution report received:', { 
          //   clientOrderId, 
          //   symbol, 
          //   execPrice, 
          //   execQty, 
          //   execSide, 
          //   orderStatus,
          //   ordStatus: ordStatusValue,
          //   leavesQty: exec.leavesQty || obj.leavesQty,
          //   cumQty: exec.cumQty || obj.cumQty,
          //   raw: obj
          // });
          
          // If backend sent complete order with fills, use it directly (new approach)
          if (obj.order && typeof obj.order === 'object') {
            const completeOrder = obj.order;
            const orderId = completeOrder.id || clientOrderId;
            
            if (orderId) {
              setOrders(prev => {
                const existingIndex = prev.findIndex(o => o.id === orderId);
                const orderFromBackend: Order = {
                  id: orderId,
                  exchangeOrderId: completeOrder.exchangeOrderId,
                  symbol: completeOrder.symbol || symbol,
                  side: (completeOrder.side || execSide).toLowerCase() as 'buy' | 'sell',
                  qty: completeOrder.qty || Number(exec.quantity || obj.quantity || (cumQty + leavesQty) || execQty || 0),
                  price: completeOrder.price || execPrice,
                  status: (completeOrder.status || orderStatus).toLowerCase().replace('_', '_') as 'new' | 'filled' | 'rejected' | 'partially_filled',
                  time: completeOrder.time || fillTime,
                  fills: completeOrder.fills || [],
                  cumQty: completeOrder.cumQty ?? cumQty,
                  leavesQty: completeOrder.leavesQty ?? leavesQty,
                  avgFillPrice: completeOrder.avgFillPrice,
                  lastFillTime: completeOrder.lastFillTime
                };
                
                if (existingIndex >= 0) {
                  const updated = [...prev];
                  updated[existingIndex] = orderFromBackend;
                  return updated;
                } else {
                  return [orderFromBackend, ...prev];
                }
              });
              
              // Add to trades if there's a fill
              if (execPrice > 0 && execQty > 0) {
                const newTrade: Trade = {
                  time: fillTime,
                  price: execPrice,
                  size: execQty,
                  side: execSide,
                  symbol: symbol
                };
                
                const currTrades = tradesBySecIdRef.current.get(symbol) || [];
                const updatedTrades = [newTrade, ...currTrades].slice(0, 100);
                tradesBySecIdRef.current.set(symbol, updatedTrades);
                if (symbol === selectedSymbol) {
                  setRecentTrades(updatedTrades.slice(0, 10));
                }
                
                if (symbol === selectedSymbol && execPrice > 0) {
                  setCurrentPrice(execPrice);
                }
              }
              
              return; // Done - used complete order from backend
            }
          }
          
          // Fallback: Update order status and track fills manually (backward compatibility)
          // Create order if it doesn't exist (in case ORDER_ACK wasn't received)
          if (clientOrderId && symbol && execPrice > 0 && execQty > 0) {
            setOrders(prev => {
              const existingIndex = prev.findIndex(o => o.id === clientOrderId);
              const mappedStatus = orderStatus === 'FILLED' || orderStatus === 'filled' ? 'filled' : 
                                  orderStatus === 'PARTIALLY_FILLED' || orderStatus === 'partially_filled' ? 'partially_filled' : 
                                  orderStatus === 'REJECTED' || orderStatus === 'rejected' ? 'rejected' :
                                  orderStatus === 'CANCELLED' || orderStatus === 'cancelled' ? 'rejected' :
                                  orderStatus === 'NEW' || orderStatus === 'new' ? 'new' : orderStatus;
              
              if (existingIndex >= 0) {
                // Update existing order with fill tracking
                const updated = [...prev];
                const existingOrder = updated[existingIndex];
                
                // Initialize fill tracking if not present (backward compatibility)
                const existingFills = existingOrder.fills || [];
                const previousCumQty = existingOrder.cumQty ?? 0;
                
                // Calculate fill quantity increment
                // The 'quantity' field in execution report is the fill increment for this event
                // If not provided, calculate from cumQty difference
                let fillQuantity = fillQty > 0 ? fillQty : (cumQty - previousCumQty);
                
                // If calculated increment is invalid or negative, use cumQty as fallback (for first fill)
                if (fillQuantity <= 0) {
                  fillQuantity = previousCumQty === 0 ? cumQty : execQty;
                }
                
                // Create new fill record
                const newFill: OrderFill = {
                  fillId: exec.fillId || `${clientOrderId}_${fillTime}`,
                  quantity: fillQuantity,
                  price: execPrice,
                  time: fillTime,
                  cumQty: cumQty,
                  leavesQty: leavesQty,
                  value: fillValue
                };
                
                // Add fill to array (avoid duplicates by checking time and quantity)
                const isDuplicate = existingFills.some(f => 
                  f.time === fillTime && f.quantity === fillQuantity && f.price === execPrice
                );
                
                const updatedFills = isDuplicate ? existingFills : [...existingFills, newFill].sort((a, b) => 
                  new Date(a.time).getTime() - new Date(b.time).getTime()
                );
                
                // Calculate weighted average fill price
                const totalValue = updatedFills.reduce((sum, f) => sum + (f.quantity * f.price), 0);
                const totalFilled = updatedFills.reduce((sum, f) => sum + f.quantity, 0);
                const avgFillPrice = totalFilled > 0 ? totalValue / totalFilled : undefined;
                
                // Determine status based on leavesQty
                const finalStatus = leavesQty === 0 ? 'filled' : 
                                   cumQty > 0 ? 'partially_filled' : 
                                   mappedStatus as 'new' | 'filled' | 'rejected' | 'partially_filled';
                
                updated[existingIndex] = {
                  ...existingOrder,
                  status: finalStatus,
                  fills: updatedFills,
                  cumQty: cumQty,
                  leavesQty: leavesQty,
                  avgFillPrice: avgFillPrice,
                  lastFillTime: fillTime
                };
                return updated;
              } else {
                // Create new order from execution report (if ORDER_ACK wasn't received)
                // Use original order quantity from message or calculate from cumQty + leavesQty
                const orderQty = Number(exec.quantity || obj.quantity || (cumQty + leavesQty) || execQty || 0);
                
                // Create fill record
                const newFill: OrderFill = {
                  fillId: exec.fillId || `${clientOrderId}_${fillTime}`,
                  quantity: execQty,
                  price: execPrice,
                  time: fillTime,
                  cumQty: cumQty,
                  leavesQty: leavesQty,
                  value: fillValue
                };
                
                const avgFillPrice = execQty > 0 ? execPrice : undefined;
                const finalStatus = leavesQty === 0 ? 'filled' : 
                                   cumQty > 0 ? 'partially_filled' : 
                                   mappedStatus as 'new' | 'filled' | 'rejected' | 'partially_filled';
                
                const newOrder: Order = {
                  id: clientOrderId,
                  exchangeOrderId: exec.orderId || obj.orderId || undefined,
                  symbol: symbol,
                  side: execSide,
                  qty: orderQty,
                  price: execPrice || 0,
                  status: finalStatus,
                  time: fillTime,
                  fills: [newFill],
                  cumQty: cumQty,
                  leavesQty: leavesQty,
                  avgFillPrice: avgFillPrice,
                  lastFillTime: fillTime
                };
                return [newOrder, ...prev];
              }
            });
            
            // Only add to trades if there's an actual execution (cumQty > 0)
            // For trades, we use the fill quantity increment, not cumulative
            if (execPrice > 0 && execQty > 0) {
              // Add to recent trades - use fill quantity increment
              const newTrade: Trade = {
                time: fillTime,
                price: execPrice,
                size: execQty,
                side: execSide,
                symbol: symbol
              };
              
              const currTrades = tradesBySecIdRef.current.get(symbol) || [];
              const updatedTrades = [newTrade, ...currTrades].slice(0, 100);
              tradesBySecIdRef.current.set(symbol, updatedTrades);
              if (symbol === selectedSymbol) {
                setRecentTrades(updatedTrades.slice(0, 10));
              }
              
              // Update current price if this is the selected symbol
              if (symbol === selectedSymbol && execPrice > 0) {
                setCurrentPrice(execPrice);
              }
            }
            
            // Note: Position updates should come from POSITION_UPDATE messages, not EXECUTION_REPORT
            // EXECUTION_REPORT is only for order/trade tracking. Position calculations are handled
            // by the backend and sent via POSITION_UPDATE messages which contain the authoritative
            // quantity, avgPrice, unrealizedPnL, realizedPnL, and totalPnL values.
          }
          return;
        }
        
        // Handle POSITION_UPDATE (position update from VPS)
        if (obj?.kind === 'POSITION_UPDATE' && obj.secId && obj.position) {
          const secId = (obj.secId || '').toUpperCase();
          const symbol = obj.symbol || secId;
          const pos = obj.position;
          
          const positionUpdate: Position = {
            symbol: symbol,
            quantity: Number(pos.quantity || 0),
            avgPrice: Number(pos.avgPrice || 0),
            currentPrice: Number(pos.currentPrice || 0) || undefined,
            unrealizedPnL: Number(pos.unrealizedPnL || 0),
            realizedPnL: Number(pos.realizedPnL || 0),
            totalPnL: Number(pos.totalPnL || (pos.realizedPnL || 0) + (pos.unrealizedPnL || 0)) || undefined,
            exposure: Number(pos.exposure || 0) || undefined,
            returnPercent: Number(pos.returnPercent || 0) || undefined,
            lastUpdate: obj.timestamp || new Date().toISOString()
          };
          
          // console.log('[MarketContext] 📊 Position update received:', {
          //   symbol,
          //   quantity: positionUpdate.quantity,
          //   avgPrice: positionUpdate.avgPrice,
          //   unrealizedPnL: positionUpdate.unrealizedPnL,
          //   realizedPnL: positionUpdate.realizedPnL
          // });
          
          setPositions(prev => {
            const existingIndex = prev.findIndex(p => p.symbol === symbol);
            if (existingIndex >= 0) {
              // Update existing position
              const updated = [...prev];
              updated[existingIndex] = positionUpdate;
              return updated;
            } else {
              // Add new position (only if quantity is not zero)
              if (positionUpdate.quantity !== 0) {
                return [...prev, positionUpdate];
              }
              return prev;
            }
          });
          
          // Update currentPosition if this is the selected symbol
          if (symbol === selectedSymbol) {
            setCurrentPosition(positionUpdate.quantity);
          }
          
          return;
        }
        
        // Handle PORTFOLIO_TOTALS (portfolio totals and risk metrics from backend)
        if (obj?.kind === 'PORTFOLIO_TOTALS') {
          const portfolioData = obj.portfolio;
          const riskData = obj.risk;
          
          if (portfolioData) {
            setPortfolio({
              totalUnrealizedPnL: Number(portfolioData.totalUnrealizedPnL || 0),
              totalRealizedPnL: Number(portfolioData.totalRealizedPnL || 0),
              totalPnL: Number(portfolioData.totalPnL || 0),
              totalExposure: Number(portfolioData.totalExposure || 0)
            });
          }
          
          if (riskData) {
            setRisk({
              grossNotional: Number(riskData.grossNotional || 0),
              longExposure: Number(riskData.longExposure || 0),
              shortExposure: Number(riskData.shortExposure || 0)
            });
          }
          
          // console.log('[MarketContext] 📊 Portfolio totals received:', {
          //   portfolio: portfolioData,
          //   risk: riskData
          // });
          
          return;
        }
      } catch {
        // fall through to FIX parsing if not JSON
      }
    }

    // FIX parsing
    if (!rawMessage.includes("8=")) return;
    const fix = extractFix(rawMessage);
    const parsed = parseFixMessage(fix);

    // Log message
    setMessages(prev => [
      { timestamp: new Date().toLocaleTimeString(), raw: rawMessage, parsed },
      ...prev.slice(0, 19)
    ]);

    // Identify instrument
    const secId = (parsed["48"] || parsed["55"] || "").trim().toUpperCase();
    const msgType = parsed["35"];
    const entries: any[] = parsed.entries || [];

    if (!secId) return;

    // Handle execution reports
    if (msgType === "8") {
      const px = parseFloat(parsed["44"] || "0");
      const qty = parseInt(parsed["38"] || "0", 10);
      const side = parsed["54"] === "1" ? "buy" : "sell";

      const newTrade: Trade = { time: new Date().toISOString(), price: px, size: qty, side, symbol: secId };
      // persist per-symbol recent trades
      const currTrades = tradesBySecIdRef.current.get(secId) || [];
      const updatedTrades = [newTrade, ...currTrades].slice(0, 100);
      tradesBySecIdRef.current.set(secId, updatedTrades);
      if (secId === selectedSymbol) {
        setRecentTrades(updatedTrades.slice(0, 10));
      }
      if (Number.isFinite(px) && secId === selectedSymbol) setCurrentPrice(px);
      return;
    }

    // Handle stats
    const STAT_TYPES = new Set(['2','4','5','7','8','9','B','K','P','e']);
    const statEntries = entries.filter(e => STAT_TYPES.has(e.type));
    if (secId && statEntries.length) {
      const curr = ensureStats(secId);
      const next: Stats = { ...curr };

      for (const e of statEntries) {
        const px = typeof e.price === 'number' ? e.price : undefined;
        const sz = typeof e.size === 'number' ? e.size : undefined;

        switch (e.type) {
          case '2': // LAST
            if (px != null) next.last = px;
            if (e.time) next.lastTime = e.time;
            if (px != null && sz != null && sz > 0) {
              // VWAP comes from VPS
              next._cumPxSz += px * sz;
              next._cumSz += sz;
              // Removed: VWAP comes from VPS
              // if (next.vwap == null && next._cumSz > 0) {
              //   next.vwap = next._cumPxSz / next._cumSz;
              // }
              if (next.volume == null) {
                next.volume = next._cumSz;
              }
            }
            break;
          case '4': if (px != null) next.open = px; break;
          case '5': if (px != null) next.close = px; break;
          case '7': if (px != null) next.high = px; break;
          case '8': if (px != null) next.low = px; break;
          case '9': if (px != null) next.vwap = px; break;
          case 'K': if (px != null) next.close = px; break;
          case 'P': if (px != null) next.prevClose = px; break;
          case 'e': if (px != null) next.prevClose = px; break;
          case 'B': if (sz != null) next.volume = sz; break;
        }
      }

      statsBySecIdRef.current.set(secId, next);
      if (secId === selectedSymbol) {
        setStats(next);
      }
    }

    // Update price chart
    const book = ensureBook(secId);
    const { mid } = bestPrices(book);
    const s = statsBySecIdRef.current.get(secId);
    const pxForChart = (s?.vwap ?? s?.last ?? s?.prevClose ?? mid);

    if (pxForChart != null) {
      appendChartPoint(secId, pxForChart);
    }

    // Handle order book updates
    const bookEntries = entries.filter(e => e.type === "0" || e.type === "1");
    if (bookEntries.length === 0) return;

    const prevBook = ensureBook(secId);
    const next = msgType === "W" ? applySnapshot(prevBook, bookEntries) :
                 msgType === "X" ? applyIncremental(prevBook, bookEntries) : null;

    if (!next) return;

    booksBySecIdRef.current.set(secId, next);

    const midPrice = bestPrices(next).mid;
    // if (midPrice != null) setPriceMap(pm => ({ ...pm, [secId]: midPrice }));
    const sNext = statsBySecIdRef.current.get(secId);
    // store the priceMap using VWAP if available, otherwise midPrice
    const priceToStore = (sNext?.vwap ?? midPrice);
    if (priceToStore != null) setPriceMap(pm => ({ ...pm, [secId]: priceToStore }));

    if (secId === selectedSymbol) {
      const { bids, asks } = bookToArrays(next);
      setOrderBook({
        bids: bids.map(b => ({ type: "0", price: b.price, size: b.size })),
        asks: asks.map(a => ({ type: "1", price: a.price, size: a.size })),
      });
      const isoNow = new Date().toISOString();
      lastUpdateBySecIdRef.current.set(secId, isoNow);
      setLastUpdateTime(isoNow);
      if (midPrice != null) setCurrentPrice(midPrice);
    }
  }, [selectedSymbol, ensureBook, ensureStats, appendChartPoint, extractFix]);

  // Replay manager setup
  const replayManagerRef = useRef<ReplayManager | null>(null);
  const recoveryTriggeredRef = useRef(false);
  
  const handleReconnect = useCallback(async () => {
    // Wait a bit for replay manager to be initialized if needed
    let attempts = 0;
    while (!replayManagerRef.current && attempts < 20) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    if (replayManagerRef.current) {
      // Prevent duplicate recoveries
      if (recoveryTriggeredRef.current) {
        // console.log('[MarketContext] ⚠️ Recovery already triggered, skipping...');
          return;
        }
        
        recoveryTriggeredRef.current = true;
      // console.log('[MarketContext] 🔄 Starting recovery after connection...');
      
      try {
        await replayManagerRef.current.recoverAfterReconnect();
        // console.log('[MarketContext] ✅ Recovery completed');
      } catch (error) {
        console.error('[MarketContext] ❌ Error during recovery:', error);
        recoveryTriggeredRef.current = false; // Allow retry on error
      }
    } else {
      // console.warn('[MarketContext] ⚠️ Replay manager not initialized, skipping recovery');
    }
  }, []);

  // Get auth token for WebSocket connection
  const { token } = useAuth();
  
  // WebSocket connection with replay support
  const { 
    sendFixMessage, 
    sendReplayRequest, 
    getLastMessageTimestamp,
    testConnection
  } = useFixWebSocket(handleFixMessage, setConnectionStatus, undefined, handleReconnect, token);

  // Reset recovery flag when disconnected
  useEffect(() => {
    if (connectionStatus === 'disconnected') {
      recoveryTriggeredRef.current = false;
      // console.log('[MarketContext] 🔄 Reset recovery flag on disconnect');
    }
  }, [connectionStatus]);

  // Initialize replay manager
  useEffect(() => {
    if (!sendReplayRequest) return;

    const securities = INSTRUMENTS.map(i => i.secId);
    
    replayManagerRef.current = new ReplayManager({
      sendReplayRequest,
      getLastMessageTimestamp,
      securities,
      onOrderBookReconstructed: (secId, book) => {
        // Update the book state for this security
        booksBySecIdRef.current.set(secId, book);
        
        // If this is the selected symbol, update the UI
        if (secId === selectedSymbol) {
          const { bids, asks } = bookToArrays(book);
          setOrderBook({
            bids: bids.map(b => ({ type: "0", price: b.price, size: b.size })),
            asks: asks.map(a => ({ type: "1", price: a.price, size: a.size })),
          });
          const { mid } = bestPrices(book);
          if (mid != null) setCurrentPrice(mid);
        }
      },
      onChartDataLoaded: (secId, stream, data) => {
        // console.log(`[MarketContext] 📊 Processing ${data.length} ${stream} messages for ${secId}`);
        
        // Process historical chart data from replay
        // For replay data, we REPLACE existing data (not merge) to reconstruct charts
        if (stream === 'fix:twap') {
          // Process TWAP data - data is already filtered by secId in the replay request
          const twapData = data
            .map((msg: any) => {
              // Handle timestamp - could be in various formats
              let ts: string;
              if (msg.timestamp) {
                if (typeof msg.timestamp === 'string') {
                  ts = msg.timestamp;
                } else if (msg.timestamp < 1e12) {
                  // Unix seconds
                  ts = new Date(msg.timestamp * 1000).toISOString();
                } else {
                  // Unix milliseconds
                  ts = new Date(msg.timestamp).toISOString();
                }
              } else if (msg.id) {
                // Try to extract timestamp from message ID if available
                const idParts = String(msg.id).split('-');
                if (idParts[0] && Number.isFinite(parseFloat(idParts[0]))) {
                  const tsNum = parseFloat(idParts[0]);
                  ts = tsNum < 1e12 ? new Date(tsNum * 1000).toISOString() : new Date(tsNum).toISOString();
                } else {
                  ts = new Date().toISOString();
                }
              } else {
                ts = new Date().toISOString();
              }
              
              return {
                timestamp: ts,
                twap1m: msg.twaps?.['1m'] ?? msg.twap1m,
                twap5m: msg.twaps?.['5m'] ?? msg.twap5m,
                twap15m: msg.twaps?.['15m'] ?? msg.twap15m,
                twap30m: msg.twaps?.['30m'] ?? msg.twap30m,
                twap1h: msg.twaps?.['1hr'] ?? msg.twaps?.['1h'] ?? msg.twap1h,
                twap4h: msg.twaps?.['4hr'] ?? msg.twaps?.['4h'] ?? msg.twap4h,
                lastPrice: msg.lastPrice ?? msg.last_price
              };
            })
            .filter((d: any) => d.timestamp)
            .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

          if (twapData.length > 0) {
            // console.log(`[MarketContext] ✅ Storing ${twapData.length} TWAP points for ${secId}`);
            setTwapDataBySymbol(prev => {
              const newMap = new Map(prev);
              // REPLACE with historical data (not merge) for reconstruction
              newMap.set(secId, twapData);
              
              setDataVersions(prev => ({
                ...prev,
                [secId]: {
                  ...prev[secId],
                  twap: ((prev[secId]?.twap || 0) + 1)
                }
              }));
              
              return newMap;
            });
          }
        } else if (stream === 'fix:vwamp') {
          // Process VWAMP data - data is already filtered by secId in the replay request
          const vwampData = data
            .map((msg: any) => {
              let ts: string;
              if (msg.timestamp) {
                if (typeof msg.timestamp === 'string') {
                  ts = msg.timestamp;
                } else if (msg.timestamp < 1e12) {
                  ts = new Date(msg.timestamp * 1000).toISOString();
                } else {
                  ts = new Date(msg.timestamp).toISOString();
                }
              } else if (msg.id) {
                const idParts = String(msg.id).split('-');
                if (idParts[0] && Number.isFinite(parseFloat(idParts[0]))) {
                  const tsNum = parseFloat(idParts[0]);
                  ts = tsNum < 1e12 ? new Date(tsNum * 1000).toISOString() : new Date(tsNum).toISOString();
                } else {
                  ts = new Date().toISOString();
                }
              } else {
                ts = new Date().toISOString();
              }
              
              return {
                timestamp: ts,
                microprice: msg.vwamp ?? msg.microprice,
                imbalance: msg.imbalance ?? 0,
                spread: msg.spread,
                bidPrice: msg.bidPrice ?? msg.bid_price,
                askPrice: msg.askPrice ?? msg.ask_price
              };
            })
            .filter((d: any) => d.timestamp && Number.isFinite(d.microprice))
            .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

          if (vwampData.length > 0) {
            // console.log(`[MarketContext] ✅ Storing ${vwampData.length} VWAMP points for ${secId}`);
            setMicropriceDataBySymbol(prev => {
              const newMap = new Map(prev);
              // REPLACE with historical data (not merge) for reconstruction
              newMap.set(secId, vwampData);
              
              setDataVersions(prev => ({
                ...prev,
                [secId]: {
                  ...prev[secId],
                  microprice: ((prev[secId]?.microprice || 0) + 1)
                }
              }));
              
              return newMap;
            });
          }
        } else if (stream === 'fix:marketstats') {
          // Process market stats data - extract VWAP and last price for charts
          const vwapData: Array<{ timestamp: string; vwap: number }> = [];
          const lastPriceData: Array<{ timestamp: string; lastPrice: number }> = [];
          
          data.forEach((msg: any) => {
            // Update stats
            if (msg.stats) {
              const stats = ensureStats(secId);
              const updatedStats: Stats = { ...stats };
              if (Number.isFinite(msg.stats.vwap)) updatedStats.vwap = Number(msg.stats.vwap);
              if (Number.isFinite(msg.stats.last)) updatedStats.last = Number(msg.stats.last);
              if (Number.isFinite(msg.stats.volume)) updatedStats.volume = Number(msg.stats.volume);
              if (Number.isFinite(msg.stats.open)) updatedStats.open = Number(msg.stats.open);
              if (Number.isFinite(msg.stats.high)) updatedStats.high = Number(msg.stats.high);
              if (Number.isFinite(msg.stats.low)) updatedStats.low = Number(msg.stats.low);
              if (Number.isFinite(msg.stats.close)) updatedStats.close = Number(msg.stats.close);
              if (Number.isFinite(msg.stats.prev_close)) updatedStats.prevClose = Number(msg.stats.prev_close);
              statsBySecIdRef.current.set(secId, updatedStats);
              
              if (secId === selectedSymbol) {
                setStats(updatedStats);
                if (Number.isFinite(msg.stats.last)) setCurrentPrice(Number(msg.stats.last));
              }
            }
            
            // Extract timestamp (shared for both VWAP and last price)
            let ts: string;
            if (msg.timestamp) {
              if (typeof msg.timestamp === 'string') {
                ts = msg.timestamp;
              } else if (msg.timestamp < 1e12) {
                ts = new Date(msg.timestamp * 1000).toISOString();
              } else {
                ts = new Date(msg.timestamp).toISOString();
              }
            } else if (msg.id) {
              const idParts = String(msg.id).split('-');
              if (idParts[0] && Number.isFinite(parseFloat(idParts[0]))) {
                const tsNum = parseFloat(idParts[0]);
                ts = tsNum < 1e12 ? new Date(tsNum * 1000).toISOString() : new Date(tsNum).toISOString();
              } else {
                ts = new Date().toISOString();
              }
            } else {
              ts = new Date().toISOString();
            }
            
            // Extract VWAP for chart
            if (Number.isFinite(msg.stats?.vwap)) {
              vwapData.push({
                timestamp: ts,
                vwap: Number(msg.stats.vwap)
              });
            }
            
            // Extract last price for chart
            if (Number.isFinite(msg.stats?.last)) {
              lastPriceData.push({
                timestamp: ts,
                lastPrice: Number(msg.stats.last)
              });
            }
          });
          
          if (vwapData.length > 0) {
            // Sort and deduplicate VWAP data
            const sortedVwap = vwapData
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
              .filter((v, i, a) => a.findIndex(t => t.timestamp === v.timestamp) === i);
            
            // console.log(`[MarketContext] ✅ Storing ${sortedVwap.length} VWAP points from market stats for ${secId}`);
            setVwapDataBySymbol(prev => {
              const newMap = new Map(prev);
              // REPLACE with historical data (not merge) for reconstruction
              newMap.set(secId, sortedVwap);
              
              setDataVersions(prevVersions => ({
                ...prevVersions,
                [secId]: {
                  ...prevVersions[secId],
                  vwap: ((prevVersions[secId]?.vwap || 0) + 1)
                }
              }));
              
              return newMap;
            });
          }
          
          if (lastPriceData.length > 0) {
            // Sort and deduplicate last price data
            const sortedLastPrice = lastPriceData
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
              .filter((v, i, a) => a.findIndex(t => t.timestamp === v.timestamp) === i);
            
            // console.log(`[MarketContext] ✅ Storing ${sortedLastPrice.length} last price points from market stats for ${secId}`);
            setLastPriceDataBySymbol(prev => {
              const newMap = new Map(prev);
              // REPLACE with historical data (not merge) for reconstruction
              newMap.set(secId, sortedLastPrice);
              
              setDataVersions(prevVersions => ({
                ...prevVersions,
                [secId]: {
                  twap: prevVersions[secId]?.twap || 0,
                  vwap: prevVersions[secId]?.vwap || 0,
                  microprice: prevVersions[secId]?.microprice || 0,
                  lastPrice: ((prevVersions[secId]?.lastPrice || 0) + 1)
                }
              }));
              
              return newMap;
            });
          }
        } else if (stream === 'fix:bookchanges') {
          // Process book changes data - convert to BookChangePacket format
          const bookChangesPackets: BookChangePacket[] = data
            .map((msg: any) => {
              // Handle timestamp
              let ts: string;
              if (msg.timestamp) {
                if (typeof msg.timestamp === 'string') {
                  ts = msg.timestamp;
                } else if (msg.timestamp < 1e12) {
                  ts = new Date(msg.timestamp * 1000).toISOString();
                } else {
                  ts = new Date(msg.timestamp).toISOString();
                }
              } else if (msg.id) {
                const idParts = String(msg.id).split('-');
                if (idParts[0] && Number.isFinite(parseFloat(idParts[0]))) {
                  const tsNum = parseFloat(idParts[0]);
                  ts = tsNum < 1e12 ? new Date(tsNum * 1000).toISOString() : new Date(tsNum).toISOString();
                } else {
                  ts = new Date().toISOString();
                }
              } else {
                ts = new Date().toISOString();
              }
              
              // Ensure we have the required fields
              // Replay response uses sec_id (with underscore), not secId
              // Check sec_id first (from replay), then secId, then fallback to parameter
              const packetSecId = (msg.sec_id || msg.secId || secId).toUpperCase();
              if (packetSecId !== secId.toUpperCase() && msg.sec_id) {
                // console.log(`[MarketContext] ⚠️ secId mismatch: packetSecId=${packetSecId}, expected=${secId}, msg.sec_id=${msg.sec_id}`);
              }
              // Handle changes - could be directly in msg or in msg.data
              let changes = Array.isArray(msg.changes) ? msg.changes : [];
              if (changes.length === 0 && msg.data && Array.isArray(msg.data.changes)) {
                changes = msg.data.changes;
              }
              
              // Map changes to the expected format
              const mappedChanges = changes.map((c: any) => {
                const side = (c.side || 'BID').toUpperCase() as 'BID' | 'ASK';
                const price = Number(c.price) || 0;
                const oldVal = Number(c.old) || Number(c.previous) || Number(c.prev) || 0;
                const newVal = Number(c.new) || Number(c.size) || 0;
                
                return {
                  side,
                  price,
                  old: oldVal,
                  new: newVal
                };
              }).filter((c: any) => c.price > 0 && (c.old !== c.new));
              
              return {
                kind: 'BOOK_CHANGES' as const,
                secId: packetSecId,
                symbol: msg.symbol || msg.secId,
                timestamp: ts,
                changes: mappedChanges
              } as BookChangePacket;
            })
            .filter((pkt: BookChangePacket) => 
              pkt.secId && pkt.changes && pkt.changes.length > 0
            )
            .sort((a: BookChangePacket, b: BookChangePacket) => {
              const tsA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
              const tsB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
              return tsA - tsB; // Oldest first, then we'll reverse for display
            });

          if (bookChangesPackets.length > 0) {
            // console.log(`[MarketContext] ✅ Storing ${bookChangesPackets.length} book change packets for ${secId}`);
            // console.log(`[MarketContext] Sample packet:`, bookChangesPackets[0]);
            // console.log(`[MarketContext] Packets with changes:`, bookChangesPackets.filter(p => p.changes.length > 0).length);
            
            // Group by security and replace all book changes for each security
            setBookChanges(prev => {
              // Remove existing packets for this security
              const filtered = prev.filter(pkt => pkt.secId?.toUpperCase() !== secId.toUpperCase());
              
              // Add new historical packets (oldest first, will be reversed in component)
              // Reverse to get newest first for display
              const reversed = [...bookChangesPackets].reverse();
              
              // Combine: new historical data first, then other securities' data
              const combined = [...reversed, ...filtered];
              // console.log(`[MarketContext] Total book changes after merge: ${combined.length} (${reversed.length} new for ${secId})`);
              return combined;
            });
          } else {
            // console.warn(`[MarketContext] ⚠️ No valid book change packets for ${secId} after processing`);
          }
        } else if (stream === 'fix:positions') {
          // Process positions snapshot (backend already deduplicated)
          // console.log(`[MarketContext] 📊 Processing ${data.length} positions from snapshot`);
          
          // Backend returns positions already deduplicated (latest state per symbol)
          // No need to deduplicate again - just map to Position format
          const positionsData: Position[] = data
            .map((pos: any) => ({
              symbol: (pos.symbol || pos.secId || '').toUpperCase(),
              quantity: Number(pos.quantity || pos.qty || 0),
              avgPrice: Number(pos.avgPrice || pos.avg_price || 0),
              unrealizedPnL: Number(pos.unrealizedPnL || pos.unrealizedPnl || pos.unrealized_pnl || 0),
              realizedPnL: Number(pos.realizedPnL || pos.realizedPnl || pos.realized_pnl || 0),
              currentPrice: Number(pos.currentPrice || pos.lastPrice || pos.last_price || 0) || undefined,
              totalPnL: Number(pos.totalPnL || 
                        ((pos.unrealizedPnL || pos.unrealizedPnl || pos.unrealized_pnl || 0) + 
                         (pos.realizedPnL || pos.realizedPnl || pos.realized_pnl || 0))) || undefined,
              exposure: Number(pos.exposure || 
                        ((pos.quantity || pos.qty || 0) * (pos.currentPrice || pos.lastPrice || pos.last_price || 0))) || undefined,
              notional: Number(pos.notional || 
                        (Math.abs((pos.quantity || pos.qty || 0) * (pos.avgPrice || pos.avg_price || 0)))) || undefined,
              returnPercent: Number(pos.returnPercent || pos.return_percent || 0) || undefined,
              lastUpdate: pos.timestamp || pos.lastUpdate || new Date().toISOString()
            }))
            .filter((p: any) => p.symbol && p.symbol.length > 0) as Position[];
          
          if (positionsData.length > 0) {
            // console.log(`[MarketContext] ✅ Restoring ${positionsData.length} positions from snapshot:`, 
            //   positionsData.map(p => `${p.symbol}: ${p.quantity}`).join(', '));
            setPositions(positionsData);
            
            // Calculate current position for selected symbol
            const selectedPosition = positionsData.find(p => p.symbol === selectedSymbol);
            if (selectedPosition) {
              setCurrentPosition(selectedPosition.quantity);
            }
          } else {
            // console.warn(`[MarketContext] ⚠️ No valid positions found in snapshot (${data.length} positions processed)`);
          }
        } else if (stream === 'fix:positions:incremental') {
          // Process incremental position updates (apply to existing state)
          // console.log(`[MarketContext] 📊 Processing ${data.length} incremental position updates`);
          
          setPositions(prev => {
            const updated = [...prev];
            let changed = false;
            
            data.forEach((msg: any) => {
              // Replay messages come as { id, data: { position: {...}, ... } }
              // Extract the actual message data
              const msgData = msg.data || msg;
              
              // Extract position
              const position = msgData.position || msgData;
              const symbol = (position.symbol || msgData.symbol || msgData.secId || msgData.sec_id || '').toUpperCase();
              
              if (!symbol) return;
              
              const positionUpdate: Position = {
                symbol: symbol,
                quantity: Number(position.quantity || position.qty || 0),
                avgPrice: Number(position.avgPrice || position.avg_price || 0),
                currentPrice: Number(position.currentPrice || position.lastPrice || 0) || undefined,
                unrealizedPnL: Number(position.unrealizedPnL || position.unrealized_pnl || 0),
                realizedPnL: Number(position.realizedPnL || position.realized_pnl || 0),
                totalPnL: Number(position.totalPnL || 
                          ((position.unrealizedPnL || 0) + (position.realizedPnL || 0))) || undefined,
                exposure: Number(position.exposure || 0) || undefined,
                notional: Number(position.notional || 0) || undefined,
                returnPercent: Number(position.returnPercent || position.return_percent || 0) || undefined,
                lastUpdate: msgData.timestamp_iso || msgData.timestamp_unix || msg.timestamp || msg.timestamp_iso || new Date().toISOString()
              };
              
              const existingIndex = updated.findIndex(p => p.symbol === symbol);
              if (existingIndex >= 0) {
                updated[existingIndex] = positionUpdate;
                changed = true;
              } else {
                updated.push(positionUpdate);
                changed = true;
              }
            });
            
            if (changed) {
              // console.log(`[MarketContext] ✅ Applied ${data.length} incremental position updates`);
            }
            
            return updated;
          });
        } else if (stream === 'fix:trades') {
          // Process trades replay data
          // console.log(`[MarketContext] 📊 Processing ${data.length} trades from replay`);
          const tradesData: Trade[] = data
            .map((msg: any) => {
              // Handle timestamp
              let time: string;
              if (msg.timestamp) {
                if (typeof msg.timestamp === 'string') {
                  time = new Date(msg.timestamp).toLocaleTimeString();
                } else if (msg.timestamp < 1e12) {
                  time = new Date(msg.timestamp * 1000).toLocaleTimeString();
                } else {
                  time = new Date(msg.timestamp).toLocaleTimeString();
                }
              } else if (msg.fix_time) {
                time = new Date(msg.fix_time).toLocaleTimeString();
              } else {
                time = new Date().toLocaleTimeString();
              }
              
              return {
                time,
                price: msg.price || msg.lastPx || 0,
                size: msg.size || msg.lastQty || msg.quantity || 0,
                side: (msg.side === '1' || msg.side === 'buy' || msg.side === 'BUY') ? 'buy' as const : 'sell' as const,
                symbol: msg.symbol || msg.secId || ''
              };
            })
            .filter((t: Trade) => t.symbol && t.price > 0 && t.size > 0)
            .sort((a: Trade, b: Trade) => {
              // Sort by time descending (most recent first)
              const timeA = new Date(`2000-01-01 ${a.time}`).getTime();
              const timeB = new Date(`2000-01-01 ${b.time}`).getTime();
              return timeB - timeA;
            });
          
          if (tradesData.length > 0) {
            // console.log(`[MarketContext] ✅ Restoring ${tradesData.length} trades from replay`);
            
            // Group trades by symbol
            const tradesBySymbol = new Map<string, Trade[]>();
            tradesData.forEach(trade => {
              const symbol = trade.symbol || '';
              if (!tradesBySymbol.has(symbol)) {
                tradesBySymbol.set(symbol, []);
              }
              tradesBySymbol.get(symbol)!.push(trade);
            });
            
            // Store trades per symbol
            tradesBySymbol.forEach((trades, symbol) => {
              tradesBySecIdRef.current.set(symbol, trades);
            });
            
            // Update recent trades for selected symbol
            const selectedTrades = tradesBySecIdRef.current.get(selectedSymbol) || [];
            setRecentTrades(selectedTrades.slice(0, 10));
          }
        } else if (stream === 'fix:orders') {
          // Process orders snapshot (backend already deduplicated)
          // console.log(`[MarketContext] 📊 Processing ${data.length} orders from snapshot`);
          
          // Backend returns orders already deduplicated (latest state per order ID)
          // No need to deduplicate again - just map to Order format
          const ordersData: Order[] = data
            .map((order: any) => {
              // Determine order status
              let status: 'new' | 'filled' | 'rejected' | 'partially_filled' = 'new';
              const ordStatus = order.ordStatus || order.orderStatus || order.status;
              if (ordStatus === '2' || ordStatus === 'FILLED' || ordStatus === 'filled') {
                status = 'filled';
              } else if (ordStatus === '1' || ordStatus === 'PARTIALLY_FILLED' || ordStatus === 'partially_filled') {
                status = 'partially_filled';
              } else if (ordStatus === '8' || ordStatus === 'REJECTED' || ordStatus === 'rejected') {
                status = 'rejected';
              }
              
              const qty = order.orderQty || order.quantity || order.qty || 0;
              const cumQty = order.cumQty || order.filledQty || order.filledQuantity || 0;
              const leavesQty = order.leavesQty ?? (qty - cumQty);
              
              // Handle timestamp
              let time: string;
              if (order.timestamp || order.time) {
                const ts = order.timestamp || order.time;
                if (typeof ts === 'string') {
                  time = ts;
                } else if (ts < 1e12) {
                  time = new Date(ts * 1000).toISOString();
                } else {
                  time = new Date(ts).toISOString();
                }
              } else {
                time = new Date().toISOString();
              }
              
              // Build fills array (backend sends complete order with fills)
              const fills: OrderFill[] = [];
              if (order.fills && Array.isArray(order.fills)) {
                fills.push(...order.fills);
              } else if (cumQty > 0) {
                // Fallback: create synthetic fill if fills not provided
                fills.push({
                  quantity: cumQty,
                  price: order.avgFillPrice || order.price || 0,
                  time: time,
                  cumQty: cumQty,
                  leavesQty: leavesQty
                });
              }
              
              return {
                id: order.id || order.clientOrderId || order.orderId || '',
                exchangeOrderId: order.exchangeOrderId || order.exchange_order_id,
                symbol: (order.symbol || order.secId || '').toUpperCase(),
                side: (order.side === '1' || order.side === 'buy' || order.side === 'BUY') ? 'buy' as const : 'sell' as const,
                qty: qty,
                price: order.price || 0,
                status: status,
                time: time,
                fills: fills,
                cumQty: cumQty,
                leavesQty: leavesQty,
                avgFillPrice: order.avgFillPrice || (cumQty > 0 ? order.price : undefined),
                lastFillTime: order.lastFillTime || (fills.length > 0 ? fills[fills.length - 1].time : undefined)
              };
            })
            .filter((o: any) => o.id && o.symbol) as Order[];
          
          if (ordersData.length > 0) {
            // console.log(`[MarketContext] ✅ Restoring ${ordersData.length} orders from snapshot:`, 
            //   ordersData.map(o => `${o.symbol} ${o.side} ${o.qty}@${o.price} [${o.status}]`).join(', '));
            setOrders(ordersData);
          } else {
            // console.warn(`[MarketContext] ⚠️ No valid orders found in snapshot (${data.length} orders processed)`);
          }
        } else if (stream === 'fix:orders:incremental') {
          // Process incremental order updates (apply to existing state)
          // console.log(`[MarketContext] 📊 Processing ${data.length} incremental order updates`);
          
          setOrders(prev => {
            const updated = [...prev];
            let changed = false;
            
            data.forEach((msg: any) => {
              // Replay messages come as { id, data: { message_data: {...}, ... } }
              // Extract the actual message data
              const msgData = msg.data || msg;
              const message_data = msgData.message_data || {};
              
              // Extract order - prefer complete order object from backend
              let order = message_data.order;
              if (!order && message_data.execution) {
                // If no complete order, try to construct from execution report
                const exec = message_data.execution;
                order = {
                  id: exec.clientOrderId,
                  exchangeOrderId: exec.orderId,
                  symbol: exec.symbol,
                  side: exec.side,
                  qty: exec.quantity || 0,
                  price: exec.price,
                  status: exec.status,
                  cumQty: exec.cumQty || 0,
                  leavesQty: exec.leavesQty || 0,
                  time: exec.time
                };
              }
              
              if (!order) {
                // Fallback to direct message
                order = msgData.order || msgData.execution || msg;
              }
              
              // Get order ID
              const orderId = order.id || order.clientOrderId || order.orderId || msg.id || '';
              if (!orderId) return;
              
              // Determine order status
              let status: 'new' | 'filled' | 'rejected' | 'partially_filled' = 'new';
              const ordStatus = order.ordStatus || order.orderStatus || order.status;
              if (ordStatus === '2' || ordStatus === 'FILLED' || ordStatus === 'filled') {
                status = 'filled';
              } else if (ordStatus === '1' || ordStatus === 'PARTIALLY_FILLED' || ordStatus === 'partially_filled') {
                status = 'partially_filled';
              } else if (ordStatus === '8' || ordStatus === 'REJECTED' || ordStatus === 'rejected') {
                status = 'rejected';
              }
              
              const qty = order.orderQty || order.quantity || order.qty || 0;
              const cumQty = order.cumQty || order.filledQty || 0;
              const leavesQty = order.leavesQty ?? (qty - cumQty);
              
              // Handle timestamp
              let time: string;
              const ts = order.timestamp || order.time || msgData.timestamp_iso || msgData.timestamp_unix || msg.timestamp || msg.timestamp_iso;
              if (ts) {
                if (typeof ts === 'string') {
                  time = ts;
                } else if (typeof ts === 'number' && ts < 1e12) {
                  time = new Date(ts * 1000).toISOString();
                } else if (typeof ts === 'number') {
                  time = new Date(ts).toISOString();
                } else {
                  time = new Date().toISOString();
                }
              } else {
                time = new Date().toISOString();
              }
              
              // Build fills array
              const fills: OrderFill[] = [];
              if (order.fills && Array.isArray(order.fills)) {
                fills.push(...order.fills);
              } else if (cumQty > 0) {
                fills.push({
                  quantity: cumQty,
                  price: order.avgFillPrice || order.price || 0,
                  time: time,
                  cumQty: cumQty,
                  leavesQty: leavesQty
                });
              }
              
              const orderUpdate: Order = {
                id: orderId,
                exchangeOrderId: order.exchangeOrderId || order.exchange_order_id,
                symbol: (order.symbol || order.secId || '').toUpperCase(),
                side: (order.side === '1' || order.side === 'buy' || order.side === 'BUY') ? 'buy' as const : 'sell' as const,
                qty: qty,
                price: order.price || 0,
                status: status,
                time: time,
                fills: fills,
                cumQty: cumQty,
                leavesQty: leavesQty,
                avgFillPrice: order.avgFillPrice || (cumQty > 0 ? order.price : undefined),
                lastFillTime: order.lastFillTime || (fills.length > 0 ? fills[fills.length - 1].time : undefined)
              };
              
              const existingIndex = updated.findIndex(o => o.id === orderId);
              if (existingIndex >= 0) {
                updated[existingIndex] = orderUpdate;
                changed = true;
              } else {
                updated.push(orderUpdate);
                changed = true;
              }
            });
            
            if (changed) {
              // console.log(`[MarketContext] ✅ Applied ${data.length} incremental order updates`);
            }
            
            return updated;
          });
        } else if (stream === 'fix:portfoliototals') {
          // Process portfolio totals snapshot
          // console.log('[MarketContext] 📊 Processing portfolio totals from snapshot:', data);
          
          // data should be the snapshot object directly (not an array)
          const snapshot = Array.isArray(data) ? data[0] : data;
          
          if (snapshot && snapshot.portfolio && snapshot.risk) {
            setPortfolio({
              totalUnrealizedPnL: Number(snapshot.portfolio.totalUnrealizedPnL || 0),
              totalRealizedPnL: Number(snapshot.portfolio.totalRealizedPnL || 0),
              totalPnL: Number(snapshot.portfolio.totalPnL || 0),
              totalExposure: Number(snapshot.portfolio.totalExposure || 0)
            });
            
            setRisk({
              grossNotional: Number(snapshot.risk.grossNotional || 0),
              longExposure: Number(snapshot.risk.longExposure || 0),
              shortExposure: Number(snapshot.risk.shortExposure || 0)
            });
            
            // console.log('[MarketContext] ✅ Restored portfolio totals from snapshot');
          }
        }
      }
    });

    // If we're already connected when replay manager is initialized, trigger recovery
    // This handles the case where the manager initializes after connection
    if (connectionStatus === 'connected' && replayManagerRef.current && !recoveryTriggeredRef.current) {
      // Small delay to ensure everything is ready
      setTimeout(() => {
        if (replayManagerRef.current && !recoveryTriggeredRef.current) {
          // console.log('[MarketContext] 🔄 Replay manager initialized while connected, triggering recovery...');
          handleReconnect();
        }
      }, 1000);
    }

    return () => {
      // Don't reset recoveryTriggeredRef here - let it reset on unmount
    };
  }, [sendReplayRequest, getLastMessageTimestamp, selectedSymbol, ensureStats, connectionStatus, handleReconnect]);

  // Update UI when selected symbol changes
  useEffect(() => {
    const b = ensureBook(selectedSymbol);
    const { bids, asks } = bookToArrays(b);
    setOrderBook({
      bids: bids.map(x => ({ type: '0', price: x.price, size: x.size })),
      asks: asks.map(x => ({ type: '1', price: x.price, size: x.size })),
    });
    const midBp = bestPrices(b).mid;
    const s = statsBySecIdRef.current.get(selectedSymbol);
    // prefer VWAP, then mid
    const preferred = (s?.vwap ?? s?.last ?? s?.prevClose ?? midBp);
    if (preferred != null) setCurrentPrice(preferred);
  }, [selectedSymbol, ensureBook]);

  // Analytics now come directly from ANALYTICS WebSocket messages (handled in handleFixMessage)
  // No client-side calculation needed - VPS sends all pre-computed analytics

  useEffect(() => {
    const s = statsBySecIdRef.current.get(selectedSymbol);
    setStats(s ?? { _cumPxSz: 0, _cumSz: 0 });
  }, [selectedSymbol]);

  useEffect(() => {
    const s = statsBySecIdRef.current.get(selectedSymbol);
    const book = ensureBook(selectedSymbol);
    const { mid } = bestPrices(book);
    const px = (s?.vwap ?? s?.last ?? s?.prevClose ?? mid ?? priceMap[selectedSymbol]);

    const series = ensureSeries(selectedSymbol);
    if (!series.length && px != null && px > 0) {
      seedFlatSeries(selectedSymbol, px);
    } else {
      setPriceHistory(series.map(p => ({ time: fmtHHmm(p.ts), price: p.price })));
    }

    // Update symbol-specific last update time and recent trades in state
    const iso = lastUpdateBySecIdRef.current.get(selectedSymbol) || '';
    if (iso) setLastUpdateTime(iso);
    const trades = tradesBySecIdRef.current.get(selectedSymbol) || [];
    setRecentTrades(trades.slice(0, 10));
  }, [selectedSymbol, ensureBook, ensureSeries, seedFlatSeries, priceMap, currentPrice]);


  useEffect(() => {
    if (currentPrice === 0 && stats?.prevClose != null) {
      setCurrentPrice(stats.prevClose);
    }
  }, [currentPrice, stats?.prevClose]);

  // VWAP data comes from WebSocket only - no HTTP API fallback needed
  // The WebSocket provides real-time VWAP updates via MARKET_STATS and ANALYTICS messages
  // Historical data can be requested via replay if needed
  useEffect(() => {
    // VWAP data is populated from WebSocket messages in handleFixMessage
    // No need to fetch from HTTP API
    // If historical data is needed, use replay requests via WebSocket
  }, [selectedSymbol]);

  useEffect(() => {
    let stop = false;
    async function poll() {
      try {
        // if we already have a book from WS, don’t override it
        const b = booksBySecIdRef.current.get(selectedSymbol);
        const hasLive = b && (b.bids.size || b.asks.size);
        if (hasLive) return;

        const snapshot = await fetchTopLevels(selectedSymbol, 10, 'both');
        if (stop) return;

        const bids = (snapshot.bids || []).map(x => ({ type:'0', price: x.price, size: x.size }));
        const asks = (snapshot.asks || []).map(x => ({ type:'1', price: x.price, size: x.size }));

        if (bids.length || asks.length) {
          if (selectedSymbol === selectedSymbol) setOrderBook({ bids, asks });
        }
      } catch { /* ignore */ }
    }

    poll();
    const id = setInterval(poll, 3000);
    return () => { stop = true; clearInterval(id); };
  }, [selectedSymbol]);

  useEffect(() => {
    let stop = false;
    (async () => {
      // Only if no trades yet
      if ((tradesBySecIdRef.current.get(selectedSymbol) || []).length) return;

      const end = new Date();
      const start = new Date(end.getTime() - 2*60*60*1000);
      try {
        const rows = await fetchTradeSeries(selectedSymbol, start.toISOString(), end.toISOString(), '1 minute');
        if (stop) return;
        // Recreate a “recent trades” feel using bucket last price/volume
        const synthetic = rows
          .filter(r => r.price != null && r.volume > 0)
          .slice(-30)
          .map(r => ({ time: r.ts, price: r.price as number, size: r.volume, side: 'buy' as const, symbol: selectedSymbol }));
        tradesBySecIdRef.current.set(selectedSymbol, synthetic);
        setRecentTrades(synthetic.slice(0, 10));
      } catch {}
    })();
    return () => { stop = true; };
  }, [selectedSymbol]);



  // Request data function
  const onRequestData = useCallback(() => {
    const testMsg = `8=FIX.4.2|35=V|49=SHABBA|56=DSE|55=${selectedSymbol}|262=1|263=1|264=1|10=000|`;
    sendFixMessage(testMsg);
    // console.log("Sent market data request for", selectedSymbol);
  }, [selectedSymbol, sendFixMessage]);

  // Place order function
  const placeOrder = useCallback((order: {
    clientOrderId: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    orderType: 'LIMIT' | 'MARKET';
    price?: number;
    quantity: number;
  }) => {
    // console.log('[MarketContext] 🚀 placeOrder called with:', order);
    // console.log('[MarketContext] 🔌 Connection status:', connectionStatus);
    
    const orderMessage = {
      type: 'PLACE_ORDER',
      order: {
        clientOrderId: order.clientOrderId,
        symbol: order.symbol.toUpperCase(),
        side: order.side,
        orderType: order.orderType,
        price: order.price,
        quantity: order.quantity
      }
    };
    
    const jsonMessage = JSON.stringify(orderMessage);
    // console.log('[MarketContext] 📝 Order JSON to send:', jsonMessage);
    
    const sent = sendFixMessage(jsonMessage);
    
    if (sent) {
      // console.log('[MarketContext] ✅ Order sent successfully:', orderMessage);
      // Order will appear in UI only after receiving ORDER_ACK or EXECUTION_REPORT from WebSocket
    } else {
      console.error('[MarketContext] ❌ Failed to send order - sendFixMessage returned false');
      console.error('[MarketContext] Connection status:', connectionStatus);
    }
  }, [sendFixMessage, connectionStatus]);

  const contextValue: MarketContextValue = {
    selectedSymbol,
    setSelectedSymbol,
    connectionStatus,
    isAutoTrading,
    setIsAutoTrading,
    lastUpdateTime,
    currentPrice,
    stats,
    orderBook,
    priceMap,
    priceHistory,
    vwapHistory,
    analytics,
    positions,
    recentTrades,
    orders,
    messages,
    bookChanges,
    onRequestData,
    placeOrder,
    testWebSocket: testConnection,
    twapDataBySymbol,
    vwapDataBySymbol,
    lastPriceDataBySymbol,
    micropriceDataBySymbol,
    dataVersions,
    INSTRUMENTS,
    labelForSecId,
    portfolio,
    risk,
  };

  return (
    <MarketContext.Provider value={contextValue}>
      {children}
    </MarketContext.Provider>
  );
}

export function useMarket() {
  const ctx = useContext(MarketContext);
  if (!ctx) throw new Error('useMarket must be used within MarketProvider');
  return ctx;
}
